/**
 * PackagesPage — EntityTable-based Packages list (11E.4 §C).
 *
 * Replaces the legacy /admin/catalog redirect stub. Subject id→name
 * resolution is threaded into the table config so columns show subject
 * NAMES, never ids.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { EntityTable } from '@/components/entity';
import type { EntityActionKey } from '@/components/entity/types';
import {
  usePackagesList,
  useUpdatePackage,
  type PackageResponse,
  type ListPackagesPagedParams,
} from '@features/catalog/api/packages';
import { useSubjects } from '@features/catalog/api/subjects';
import { buildPackagesTableConfig } from '@features/packages/config/packagesEntityConfig';
import { ImportExportButtons } from '@features/imports';

const PAGE_SIZE = 20;

export const Packages: React.FC = () => {
  const navigate = useNavigate();

  const { data: subjectsData } = useSubjects({ status: 'active' });
  const subjectNameById = useMemo(
    () => new Map((subjectsData?.items ?? []).map((s) => [s.id, s.name])),
    [subjectsData],
  );
  const tableConfig = useMemo(() => buildPackagesTableConfig(subjectNameById), [subjectNameById]);

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(tableConfig.defaultSort ?? 'name');
  const [order, setOrder] = useState<'asc' | 'desc'>(tableConfig.defaultOrder ?? 'asc');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>(tableConfig.defaultFilters ?? {});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const queryParams: ListPackagesPagedParams = {
    status: filters.status && filters.status !== 'all' ? filters.status : undefined,
    subject_id: filters.subject_id && filters.subject_id !== 'all' ? filters.subject_id : undefined,
    search: search || undefined,
    sort,
    order,
    page,
    page_size: PAGE_SIZE,
  };

  const { data: result, isLoading, error, refetch } = usePackagesList(queryParams);
  const updateMutation = useUpdatePackage();

  const handleSortChange = (newSort: string, newOrder: 'asc' | 'desc') => {
    setSort(newSort);
    setOrder(newOrder);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleRowClick = (pkg: PackageResponse) => {
    navigate(`/packages/${pkg.id}`);
  };

  const handleRowAction = async (action: EntityActionKey, pkg: PackageResponse) => {
    if (action === 'view') {
      navigate(`/packages/${pkg.id}`);
    } else if (action === 'edit') {
      navigate(`/packages/${pkg.id}/edit`);
    } else if (action === 'archive') {
      if (!window.confirm(`Archive ${pkg.name}?`)) return;
      await updateMutation.mutateAsync({ id: pkg.id, body: { status: 'inactive' } });
    } else if (action === 'activate') {
      await updateMutation.mutateAsync({ id: pkg.id, body: { status: 'active' } });
    }
  };

  const total = result?.total ?? 0;
  const subtitle = isLoading ? 'Loading…' : `${total.toLocaleString()} packages`;

  return (
    <PageContainer
      title="Packages"
      subtitle={subtitle}
      data-testid="candeza-packages-page"
      data-flow="packages-page"
      actions={
        <>
          <ImportExportButtons
            entity="packages"
            entityLabel="Packages"
            selectedIds={selectedIds}
            exportStatus={filters.status && filters.status !== 'all' ? filters.status : undefined}
            exportSearch={search || undefined}
          />
          <Button onClick={() => navigate('/packages/new')} data-testid="candeza-packages-add-btn">
            + Add package
          </Button>
        </>
      }
    >
      <Card padding="lg">
        <EntityTable
          config={tableConfig}
          result={result}
          isLoading={isLoading}
          error={error as Error | null}
          sort={sort}
          order={order}
          search={search}
          filters={filters}
          page={page}
          selectedIds={selectedIds}
          onSortChange={handleSortChange}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          onPageChange={setPage}
          onSelectChange={setSelectedIds}
          onRowClick={handleRowClick}
          onRowAction={handleRowAction}
          onRetry={() => refetch()}
        />
      </Card>
    </PageContainer>
  );
};

